/**
 * The clinical-trials template js file
 *
 * @package  JWCI
 * @since    1.0.0
 */

/**
 * Import dependencies.
 */
import $ from 'jquery';

// The global object for the app (should already exist due to wp_localize_script, see functions/enqueue.php).
const ClinicalTrials = window.ClinicalTrials || {};

// The global wp object from wp-api.js (should already exist due to wp_enqueue_script, see functions/enqueue.php).
const wp = window.wp || {};

/**
 * Waits for wp-api to finish loading, so that we have access to 
 * wp.api.models and wp.api.collections. See functions/enqueue.php for
 * wp-api.js script.
 */
wp.api.loadPromise.done( function() {

	/**
	 * Results div.
	 */
	ClinicalTrials.resultsContainer = $( '#clinical-trials-results' );

	/**
	 * An empty object of REST API query args.
	 *
	 * @since    1.0.0
	 */
	ClinicalTrials.options = {
		data: {
			per_page: 10,
			filter: {}
		}
	};


	if ( ClinicalTrials.hasOwnProperty( 'center' ) ) {
		ClinicalTrials.options.data.filter.center = ClinicalTrials.center;
	}

	/**
	 * Extends the WordPress model for 'clinical-trial' post-type.
	 *
	 * @since    1.0.0
	 */
	ClinicalTrials.Post = wp.api.models.ClinicalTrial.extend({

		// Runs when the model is created.
		initialize: function() {
			
			// Change 'title.rendered' to title for easier templating and debugging.
			try {
				this.set( 'title', this.attributes.title.rendered );
			} catch ( e ) {
				console.log( 'Error setting the title property' );
				console.log( e );
			}
		}

	});

	/**
	 * Extends the WordPress collection for 'clinical-trial' post-type.
	 *
	 * - Overwrites the model to use `ClinicalTrials.Post` defined above.
	 * - Overwrites the URL to use for fetching posts, ClinicalTrials.ApiUrl
	 *   set from wp_localize_script ).
	 *
	 * @link     http://backbonejs.org/#Collection-model
	 * @link     http://backbonejs.org/#Collection-url
	 * @link     https://developer.wordpress.org/rest-api/using-the-rest-api/backbone-javascript-client/#using
	 * @link     https://github.com/WP-API/client-js/issues/119#issuecomment-218343611
	 *
	 * @since    1.0.0
	 */
	ClinicalTrials.PostCollection = wp.api.collections.ClinicalTrial.extend( {

		// Overwrites the model for this collection to use our slightly modified model.
		model: ClinicalTrials.Post,

		// Overwrites the URL that fetches posts to always be the root site URL. See wp_localize_script for this value.
		url: ClinicalTrials.ApiUrl,

	} );

	/**
	 * Defines a Backbone view for clinical trials table.
	 *
	 * Sets el to the existing table on the document from 
	 * partials/clinical-trials.php rather than creating a 
	 * new element and injecting it into the DOM.
	 *
	 * Listens to posts added to the collection that is 
	 * created with this view when running:
	 * `new ClinicalTrials.PostsView({ collection: myCollectionVar });`.
	 *
	 * Adds a new post view for every post returned from a collection.
	 *
	 * @since    1.0.0
	 */
	ClinicalTrials.PostsView = Backbone.View.extend({

		el: '#clinical-trials-form',

		events: {
			'click #clinical-trials-load-more' : 'morePosts',
			'click .filter--search button' : 'formSubmit',
			'submit': 'formSubmit',
			'change #js-filter-center' : 'formSubmit'
		},

		// Runs when the view is created.
		initialize: function() {
			this.listenTo( this.collection, 'add', this.addPost );
			_.bindAll( this, 'setFilters', 'morePosts', 'maybeShowLoadMore' );
		},

		// Adds a post to View when it's added to the collection.
		addPost: function( post ) {

			// Create a new view for this post.
			const postView = new ClinicalTrials.PostView({
				model      : post,           // Pass this post in as the model.
				collection : this.collection // Pass this collection as the collection.
			});
			postView.render();
		},

		formSubmit: function( e ) {
			e.preventDefault();

			const self = this;

			// Empty the models in the current colection.
			this.collection.reset();

			// Empty the HTML of posts inside the results container.
			ClinicalTrials.resultsContainer.html( '' );

			this.setFilters();

			// Fetch new posts with the new criteria.
			ClinicalTrials.options.error = this.onErrorHandler;
			this.collection.fetch( ClinicalTrials.options ).done( function( collection, response, options ) {
				if ( _.isEmpty( collection ) ) {
					self.showError();
				} else {
					self.hideError();
				}
				self.maybeShowLoadMore();
			} );
		},

		showError: function() {
			// Hide table.
			this.$( '#clinical-trials-table' ).hide();
			// Append error message if not already present.
			if ( this.$( '#filters-error-message' ).length < 1 ) {
				$( '#clinical-trials-form' ).append( '<div id="filters-error-message" class="filters-error-message">' + ClinicalTrials.strings.no_posts + '</div>' );
			}
			// Show error message.
			this.$( '#filters-error-message' ).show();
		},

		hideError: function() {
			// Hide error message.
			this.$( '#filters-error-message' ).hide();
			// Show table.
			this.$( '#clinical-trials-table' ).show();

		},

		morePosts: function( e ) {
			e.preventDefault();

			const self = this;

			if ( this.collection.hasMore() ) {
				/**
				 * Pass in the current state of the collection to the `.more()` method.
				 *
				 * This will ensure that modification to the collection's state are passed
				 * after initialization. This only happens when we want to change the collection
				 * to prepopulate it by a center term, since we create the collection first and then
				 * check if a center should be prepopulated.
				 *
				 * @link    https://github.com/WP-API/client-js/blob/master/js/collections.js#L106
				 */
				this.collection.more( this.collection.state ).done( function() {
					self.maybeShowLoadMore();
				} );
			}
		},

		maybeShowLoadMore: function() {
			
			if ( this.collection.hasMore() ) {
				$( '#clinical-trials-load-more' ).show();
			} else {
				$( '#clinical-trials-load-more' ).hide();
			}
			
		},

		setFilters: function() {

			// Set Search parameter or remove it.
			const searchInputValue = this.$( '[name="search"]' ).val();
			if ( _.isEmpty( searchInputValue ) ) {
				delete ClinicalTrials.options.data.search;
			} else {
				ClinicalTrials.options.data.search = searchInputValue;
			}

			// Set center parameter or remove it.
			const centerSelectValue = this.$( '[name="center"]' ).val();
			if ( '-1' === centerSelectValue ) {
				// Check if filter and filter.center are properties. Delete center.
				if ( ClinicalTrials.options.data.hasOwnProperty( 'filter' ) && 
					 ClinicalTrials.options.data.filter.hasOwnProperty( 'center' ) ) 
				{
					delete ClinicalTrials.options.data.filter.center;
				}
			} else {
				if ( ! ClinicalTrials.options.data.hasOwnProperty( 'filter' ) ) {
					ClinicalTrials.options.data.filter = {};
				}
				ClinicalTrials.options.data.filter.center = centerSelectValue;
			}
		}
	})

	/**
	 * Defines a Backbone view for a clinical trial post.
	 *
	 * References the table defined in partials/clinical-trials.php.
	 *
	 * Adds a new row to the table everytime a view is created running:
	 * `new ClinicalTrials.PostView({ model: postObjectFromRestAPI });`.
	 *
	 * @since    1.0.0
	 */
	ClinicalTrials.PostView = Backbone.View.extend({
		
		// The template that renderes a row in the table for the post. Requires wp-util.js to be enqueued.
		template: wp.template( 'clinical-trial-row' ),

		// The tagname used to wrap the output of the template. Sets up this.$el.
		tagName: 'tr',

		// Render method. Creates post HTML and appends it to the listing of posts.
		render: function() {

			// Stores this model's data in a json array.
			const data = this.model.toJSON();

			data.link = this.fixLink( data.link );

			// Fills the template with this data.
			const templateHTML = this.template( data );

			// Adds template HTML to this views tagName (tr).
			const viewHtml = this.$el.html( templateHTML );

			// Adds the table-row HTML from this View to the listing of posts.
			ClinicalTrials.resultsContainer.append( viewHtml );
		},

		/**
		 * Uses current site slug on the multisite network.
		 *
		 * Has no effect on primary site, but will transform
		 * subsite links from jwci.com to jwci.com/melanoma.
		 * 
		 * Used for fixing permalinks so they resolve on
		 * the subsite rather than redirecting to primary
		 * site.
		 */
		fixLink: function( url ) {
			// Return if properties aren't set.
			if ( ! url || ! ClinicalTrials.hasOwnProperty( 'homeURL' ) || ! ClinicalTrials.hasOwnProperty( 'networkHomeURL' ) ) {
				return url;
			}
			
			// Return if on primary site.
			if ( ClinicalTrials.homeURL === ClinicalTrials.networkHomeURL ) {
				return url;
			}

			// Replace home url with subsite URL.
			return url.replace( ClinicalTrials.networkHomeURL, ClinicalTrials.homeURL  + '/' );
		}
	});

	/**
	 * Initialize method to kick things off.
	 *
	 * - Creates a new collection from wp-api Clinical Trial collection.
	 * - Adds posts from ClinicalTrials variable passed from wp_localize_script. See functions/enqueue.php ,
	 *   this reduces a fetch query on page load.
	 * - Updates the collection's pagination variables so the backbone app can take over.
	 *
	 * @since    1.0.0
	 */
	ClinicalTrials.initialize = function() {

		// Creates a new post collection.
		const postCollection = new ClinicalTrials.PostCollection();

		// Passes this collection into the Posts view.
		const postsView = new ClinicalTrials.PostsView( { 
			collection: postCollection
		} );

		// Adds posts from wp_localize_script to start things off.
		postCollection.add( ClinicalTrials.query.posts );

		// Set the initial pagination variables on this collection from query passed to ClinicalTrials from wp_localize_script.
		postCollection.state.currentPage  = ClinicalTrials.query.current_page;
		postCollection.state.totalObjects = ClinicalTrials.query.found_posts;
		postCollection.state.totalPages   = ClinicalTrials.query.total_pages;

		postCollection.state.data = {};
		if ( ClinicalTrials.hasOwnProperty( 'center' ) ) {
			postCollection.state.data.filter = {
				center: ClinicalTrials.center
			};
		}
		

		// Maybe shows load more, if there are more posts in our initial query run from wp_localize_script.
		postsView.maybeShowLoadMore();
	}

	// When the document is ready, runs the initialize method.
	$( document ).ready( function() {
		ClinicalTrials.initialize();
	} );

} );